import type { SalesPoint } from 'Services/ServiceProviderManagement/Features/SalesPoint/types'
import type { ListResponse } from 'Services/ServiceProviderManagement/types'
import type { LoadingStates } from 'Store'
import { createAsyncThunkWithErrorHandling, getAxiosInstance } from 'Util/ApiClient'
import actioneGenerator from 'Util/actionGenerator'
import { createSlice } from '@reduxjs/toolkit'

export enum DeviceTabType {
  DETAILS = 'details',
  ALLOWED_API_CLIENTS = 'allowed_api_clients'
}

export interface Device {
  owner_client: string
  client_read: string[]
  client_write: string[]
  pta: string
  device_id: string
  name: string
  sales_point_id: string
  created_timestamp: Date
  modified_timestamp: Date
  last_modified_client: string
  sales_point: SalesPoint
  active: boolean
  activity_end_date: Date | null
  source: string
}

export interface DeviceState {
  total_results: number
  next_page: string | null
  previous_page: string | null
  devices: Device[]
  allDevices: Device[]
  singleDevice: Device | null
  loading: LoadingStates
  singleLoading: LoadingStates
  allDevicesLoading: LoadingStates
  deleteLoading: LoadingStates
  submitLoading: LoadingStates
}

const initialState: DeviceState = {
  total_results: 0,
  next_page: null,
  previous_page: null,
  devices: [],
  allDevices: [],
  singleDevice: null,
  loading: 'idle',
  singleLoading: 'idle',
  allDevicesLoading: 'idle',
  deleteLoading: 'idle',
  submitLoading: 'idle'
}

export interface FetchDeviceListParams {
  count?: number
  name?: string
  search_text?: string
  sort_by?: string
  sort_order?: string
  start_index?: string
  bypass_pagination?: boolean
}

const fetchDevices = createAsyncThunkWithErrorHandling<
  ListResponse<Device>,
  FetchDeviceListParams | undefined
>('device/fetchDevices', async data => {
  const { ...params } = data
  const response = await getAxiosInstance().get<ListResponse<Device>>('/api/v1/device/', {
    params
  })
  return response.data
})

const fetchAllDevices = createAsyncThunkWithErrorHandling<
  ListResponse<Device>,
  FetchDeviceListParams | undefined
>('device/fetchAllDevices', async data => {
  const { ...params } = data
  const response = await getAxiosInstance().get<ListResponse<Device>>('/api/v1/device/', {
    params
  })
  return response.data
})

const fetchSingleDevice = createAsyncThunkWithErrorHandling<Device, { id: string }>(
  'device/fetchSingleDevice',
  async data => {
    const response = await getAxiosInstance().get<Device>(`/api/v1/device/${data.id}/`)
    return response.data
  }
)

const createSingleDevice = createAsyncThunkWithErrorHandling<Device, Partial<Device>>(
  'device/createSingleDevice',
  async data => {
    const response = await getAxiosInstance().post<Device>('/api/v1/device/', data)
    return response.data
  }
)

const updateSingleDevice = createAsyncThunkWithErrorHandling<
  Device,
  { id: string } & Partial<Device>
>('device/updateSingleDevice', async data => {
  const { id, ...fields } = data
  const updatedFields = {
    ...fields,
    // activity_end_date has to be null when updating passive device
    activity_end_date: !fields.active && fields.activity_end_date ? null : fields.activity_end_date
  }
  const response = await getAxiosInstance().put<Device>(`/api/v1/device/${id}/`, updatedFields)
  return response.data
})

const genericActions = actioneGenerator('device')

export const deviceSlice = createSlice({
  name: genericActions.name,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder

      /* Device list */
      .addCase(fetchDevices.fulfilled, (state, { payload }) => {
        state.devices = payload.resources
        state.total_results = payload.total_results
        state.next_page = payload.next_page
        state.previous_page = payload.previous_page
        state.loading = 'fulfilled'
      })
      .addCase(fetchDevices.pending, state => {
        state.devices = initialState.devices
        state.total_results = initialState.total_results
        state.next_page = initialState.next_page
        state.previous_page = initialState.previous_page
        state.loading = 'pending'
      })
      .addCase(fetchDevices.rejected, state => {
        state.loading = 'rejected'
      })

      /* All devices list */
      .addCase(fetchAllDevices.fulfilled, (state, { payload }) => {
        state.allDevices = payload.resources
        state.total_results = payload.total_results
        state.allDevicesLoading = 'fulfilled'
      })
      .addCase(fetchAllDevices.pending, state => {
        state.allDevices = initialState.devices
        state.total_results = initialState.total_results
        state.allDevicesLoading = 'pending'
      })
      .addCase(fetchAllDevices.rejected, state => {
        state.allDevicesLoading = 'rejected'
      })

      /* Single device */
      .addCase(fetchSingleDevice.fulfilled, (state, { payload }) => {
        state.singleDevice = payload
        state.singleLoading = 'fulfilled'
      })
      .addCase(fetchSingleDevice.pending, state => {
        state.singleDevice = initialState.singleDevice
        state.singleLoading = 'pending'
      })
      .addCase(fetchSingleDevice.rejected, state => {
        state.singleLoading = 'rejected'
      })

      /* Update single device */
      .addCase(updateSingleDevice.fulfilled, (state, { payload }) => {
        state.singleDevice = payload
        state.submitLoading = 'fulfilled'
      })
      .addCase(updateSingleDevice.pending, state => {
        state.submitLoading = 'pending'
      })
      .addCase(updateSingleDevice.rejected, state => {
        state.submitLoading = 'rejected'
      })

      /* Create single device */
      .addCase(createSingleDevice.fulfilled, (state, { payload }) => {
        state.singleDevice = payload
        state.submitLoading = 'fulfilled'
      })
      .addCase(createSingleDevice.pending, state => {
        state.submitLoading = 'pending'
      })
      .addCase(createSingleDevice.rejected, state => {
        state.submitLoading = 'rejected'
      })
  }
})

export default deviceSlice.reducer

export { createSingleDevice, fetchAllDevices, fetchDevices, fetchSingleDevice, updateSingleDevice }
